import React from 'react';

const RecommendationsPage = () => {
  // Placeholder value for the progress ring - replace with dynamic state later
  const optimizationScorePercent = 70;
  const circumference = 2 * Math.PI * 42; // 2 * PI * radius (42)
  const strokeDashoffset = circumference - (optimizationScorePercent / 100) * circumference;

  return (
    <>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Recommendations</h2>
        <p className="text-gray-600 mt-1">Actionable insights to improve your keyword strategy and product performance</p>
      </div>

      {/* Optimization Overview */}
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Optimization Score */}
          <div className="text-center">
              <div className="mx-auto w-24 h-24 relative">
                  <svg className="w-24 h-24" viewBox="0 0 100 100">
                      {/* Background circle */}
                      <circle className="text-gray-200" strokeWidth="8" stroke="currentColor" fill="transparent" r="42" cx="50" cy="50" />
                      {/* Progress circle */}
                      <circle
                          className="text-indigo-600 progress-ring-circle"
                          strokeWidth="8"
                          strokeDasharray={circumference}
                          strokeDashoffset={strokeDashoffset} // Apply calculated offset
                          strokeLinecap="round" // Optional: makes the line ends rounded
                          stroke="currentColor"
                          fill="transparent"
                          r="42"
                          cx="50"
                          cy="50"
                          style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }} // Rotate to start from top
                      />
                  </svg>
                  <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                      <span className="text-xl font-bold text-gray-800">{optimizationScorePercent}%</span>
                  </div>
              </div>
              <h3 className="mt-3 text-lg font-medium text-gray-800">Optimization Score</h3>
              <p className="text-sm text-gray-600 mt-1">Overall keyword performance score</p>
          </div>

          {/* Key Opportunities */}
          <div className="space-y-3">
              <h3 className="text-lg font-medium text-gray-800 mb-3">Key Opportunities</h3>
              <div className="flex items-center">
                  <div className="h-2.5 w-2.5 rounded-full bg-red-500 mr-2"></div>
                  <p className="text-sm text-gray-600">28 products with below-average CTR</p>
              </div>
              <div className="flex items-center">
                  <div className="h-2.5 w-2.5 rounded-full bg-orange-500 mr-2"></div>
                  <p className="text-sm text-gray-600">15 keywords with high competition but low performance</p>
              </div>
              <div className="flex items-center">
                  <div className="h-2.5 w-2.5 rounded-full bg-green-500 mr-2"></div>
                  <p className="text-sm text-gray-600">32 new keyword opportunities identified</p>
              </div>
              <div className="flex items-center">
                  <div className="h-2.5 w-2.5 rounded-full bg-blue-500 mr-2"></div>
                  <p className="text-sm text-gray-600">5 product descriptions need optimization</p>
              </div>
          </div>

          {/* Potential Impact */}
          <div>
              <h3 className="text-lg font-medium text-gray-800 mb-3">Potential Impact</h3>
              <div className="space-y-4">
                  <div>
                      <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-medium text-gray-600">Traffic Increase</span>
                          <span className="text-sm font-medium text-gray-800">+15-20%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div className="bg-green-500 h-2.5 rounded-full" style={{width: '65%'}}></div>
                      </div>
                  </div>
                  <div>
                      <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-medium text-gray-600">CTR Improvement</span>
                          <span className="text-sm font-medium text-gray-800">+25-35%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div className="bg-blue-500 h-2.5 rounded-full" style={{width: '75%'}}></div>
                      </div>
                  </div>
                  <button className="mt-2 w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors duration-200">
                      Apply All Recommendations
                  </button>
              </div>
          </div>
        </div>
      </div>

      {/* Keyword Recommendations Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
         {/* Recommended Keywords Card */}
        <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-800">Recommended Keywords</h3>
            </div>
            <div className="p-6 space-y-4">
                 {/* Example Recommendation 1 */}
                <div className="recommendation-card bg-indigo-50 p-4 rounded-md">
                    <div className="flex justify-between items-start">
                        <div>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 mb-2">
                                High Potential
                            </span>
                            <h4 className="text-sm font-medium text-gray-900">Add "noise cancelling bluetooth earbuds"</h4>
                            <p className="text-xs text-gray-600 mt-1">For: Wireless Earbuds Pro</p>
                        </div>
                        <button className="text-indigo-600 hover:text-indigo-900 text-sm font-medium">Add</button>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                        <div><span className="block text-gray-500">Search Volume</span><span className="font-medium text-gray-800">7,850/mo</span></div>
                        <div><span className="block text-gray-500">Competition</span><span className="font-medium text-gray-800">Medium (0.5)</span></div>
                        <div><span className="block text-gray-500">Est. CPC</span><span className="font-medium text-gray-800">$1.45</span></div>
                    </div>
                </div>
                 {/* Add more example recommendations here */}
                 <div className="recommendation-card bg-indigo-50 p-4 rounded-md">
                     <div className="flex justify-between items-start">
                         <div>
                             <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mb-2">
                                 Low Competition
                             </span>
                             <h4 className="text-sm font-medium text-gray-900">Add "sustainable yoga mat eco friendly"</h4>
                             <p className="text-xs text-gray-600 mt-1">For: Bamboo Yoga Mat</p>
                         </div>
                         <button className="text-indigo-600 hover:text-indigo-900 text-sm font-medium">Add</button>
                     </div>
                     <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                         <div><span className="block text-gray-500">Search Volume</span><span className="font-medium text-gray-800">4,250/mo</span></div>
                         <div><span className="block text-gray-500">Competition</span><span className="font-medium text-gray-800">Low (0.3)</span></div>
                         <div><span className="block text-gray-500">Est. CPC</span><span className="font-medium text-gray-800">$0.85</span></div>
                     </div>
                 </div>
                 <div className="recommendation-card bg-indigo-50 p-4 rounded-md">
                     <div className="flex justify-between items-start">
                         <div>
                             <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-2">
                                 Trending
                             </span>
                             <h4 className="text-sm font-medium text-gray-900">Add "activity tracker sleep monitor waterproof"</h4>
                             <p className="text-xs text-gray-600 mt-1">For: Fitness Tracker Band</p>
                         </div>
                         <button className="text-indigo-600 hover:text-indigo-900 text-sm font-medium">Add</button>
                     </div>
                     <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                         <div><span className="block text-gray-500">Search Volume</span><span className="font-medium text-gray-800">5,850/mo</span></div>
                         <div><span className="block text-gray-500">Competition</span><span className="font-medium text-gray-800">Medium (0.6)</span></div>
                         <div><span className="block text-gray-500">Est. CPC</span><span className="font-medium text-gray-800">$1.65</span></div>
                     </div>
                 </div>
                <button className="w-full text-indigo-600 border border-indigo-600 py-2 px-4 rounded-md hover:bg-indigo-50 transition-colors duration-200 text-sm font-medium">
                    View All Recommendations
                </button>
            </div>
        </div>

        {/* Replacement Suggestions Card */}
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-800">Replacement Suggestions</h3>
          </div>
          <div className="p-6 space-y-4">
            {/* Example Replacement 1 */}
            <div className="recommendation-card bg-red-50 p-4 rounded-md">
                <div className="flex justify-between items-start">
                    <div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 mb-2">Low Performance</span>
                        <h4 className="text-sm font-medium text-gray-900">Replace "bluetooth speaker mini" with "small portable bluetooth speaker"</h4>
                        <p className="text-xs text-gray-600 mt-1">For: Bluetooth Speaker Mini</p>
                    </div>
                    <button className="text-indigo-600 hover:text-indigo-900 text-sm font-medium">Replace</button>
                </div>
                <div className="mt-3">
                    <div className="flex items-center justify-between flex-wrap gap-x-4 gap-y-1 mb-2"> {/* Added flex-wrap and gap */}
                        <div className="flex items-center">
                            <span className="text-xs text-gray-500 mr-2">Current CTR:</span>
                            <span className="text-xs font-medium text-red-500">1.2%</span>
                        </div>
                        <div className="flex items-center">
                            <span className="text-xs text-gray-500 mr-2">Estimated CTR:</span>
                            <span className="text-xs font-medium text-green-500">3.5%</span>
                        </div>
                        <div className="flex items-center">
                            <span className="text-xs text-gray-500 mr-2">Improvement:</span> {/* Shorter label */}
                            <span className="text-xs font-medium text-green-500">+191.6%</span>
                        </div>
                    </div>
                </div>
            </div>
            {/* Add more example replacements here */}
             <div className="recommendation-card bg-red-50 p-4 rounded-md">
                 <div className="flex justify-between items-start">
                     <div>
                         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 mb-2">Low Performance</span>
                         <h4 className="text-sm font-medium text-gray-900">Replace "lip balm organic" with "natural organic lip balm"</h4>
                         <p className="text-xs text-gray-600 mt-1">For: Organic Lip Balm</p>
                     </div>
                     <button className="text-indigo-600 hover:text-indigo-900 text-sm font-medium">Replace</button>
                 </div>
                 <div className="mt-3">
                     <div className="flex items-center justify-between flex-wrap gap-x-4 gap-y-1 mb-2">
                         <div className="flex items-center">
                             <span className="text-xs text-gray-500 mr-2">Current CTR:</span>
                             <span className="text-xs font-medium text-red-500">1.8%</span>
                         </div>
                         <div className="flex items-center">
                             <span className="text-xs text-gray-500 mr-2">Estimated CTR:</span>
                             <span className="text-xs font-medium text-green-500">4.2%</span>
                         </div>
                         <div className="flex items-center">
                             <span className="text-xs text-gray-500 mr-2">Improvement:</span>
                             <span className="text-xs font-medium text-green-500">+133.3%</span>
                         </div>
                     </div>
                 </div>
             </div>
             <div className="recommendation-card bg-orange-50 p-4 rounded-md">
                 <div className="flex justify-between items-start">
                     <div>
                         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 mb-2">High Competition</span>
                         <h4 className="text-sm font-medium text-gray-900">Replace "smart watch" with "fitness smart watch with heart rate"</h4>
                         <p className="text-xs text-gray-600 mt-1">For: Smart Watch Series 5</p>
                     </div>
                     <button className="text-indigo-600 hover:text-indigo-900 text-sm font-medium">Replace</button>
                 </div>
                 <div className="mt-3">
                     <div className="flex items-center justify-between flex-wrap gap-x-4 gap-y-1 mb-2">
                         <div className="flex items-center">
                             <span className="text-xs text-gray-500 mr-2">Current Comp:</span> {/* Shorter label */}
                             <span className="text-xs font-medium text-red-500">High (0.85)</span>
                         </div>
                         <div className="flex items-center">
                             <span className="text-xs text-gray-500 mr-2">New Comp:</span> {/* Shorter label */}
                             <span className="text-xs font-medium text-green-500">Medium (0.55)</span>
                         </div>
                         <div className="flex items-center">
                             <span className="text-xs text-gray-500 mr-2">Search Vol:</span> {/* Shorter label */}
                             <span className="text-xs font-medium text-gray-800">6,450/mo</span>
                         </div>
                     </div>
                 </div>
             </div>
            <button className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors duration-200 text-sm font-medium">
              Apply All Replacements
            </button>
          </div>
        </div>
      </div>

      {/* Optimization Results Card */}
      <div className="bg-white rounded-lg shadow border border-gray-200 mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-800">Optimization Results</h3>
        </div>
        <div className="p-6">
          {/* Chart Placeholder */}
          <div className="chart-container mb-6">
            <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded">
                <div className="text-center">
                    <p className="text-gray-500">Optimization Results Chart Placeholder</p>
                    <p className="text-xs text-gray-400 mt-1">Before/After comparison for optimized keywords</p>
                </div>
            </div>
          </div>
          {/* Result Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* Card 1 */}
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center mb-2">
                      <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center mr-3"><i className="fas fa-arrow-up text-green-600"></i></div>
                      <h4 className="text-lg font-medium text-gray-800">CTR Improvement</h4>
                  </div>
                  <div className="ml-11">
                      <p className="text-2xl font-bold text-gray-800">+68%</p>
                      <p className="text-sm text-gray-600">Average across optimized keywords</p>
                  </div>
              </div>
              {/* Card 2 */}
               <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                   <div className="flex items-center mb-2">
                       <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center mr-3"><i className="fas fa-users text-blue-600"></i></div>
                       <h4 className="text-lg font-medium text-gray-800">Traffic Increase</h4>
                   </div>
                   <div className="ml-11">
                       <p className="text-2xl font-bold text-gray-800">+12.5K</p>
                       <p className="text-sm text-gray-600">Monthly visits from optimized keywords</p>
                   </div>
               </div>
               {/* Card 3 */}
               <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                   <div className="flex items-center mb-2">
                       <div className="h-8 w-8 bg-yellow-100 rounded-full flex items-center justify-center mr-3"><i className="fas fa-dollar-sign text-yellow-600"></i></div>
                       <h4 className="text-lg font-medium text-gray-800">Cost Savings</h4>
                   </div>
                   <div className="ml-11">
                       <p className="text-2xl font-bold text-gray-800">$2,450</p>
                       <p className="text-sm text-gray-600">Monthly estimated ad spend saved</p>
                   </div>
               </div>
          </div>
          {/* Optimization History */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h4 className="text-sm font-medium text-gray-800 mb-3">Optimization History</h4>
             <div className="space-y-3">
                <div className="flex items-center">
                    <div className="h-2 w-2 rounded-full bg-gray-400 mr-2"></div>
                    <span className="text-sm text-gray-600 mr-3 w-28">1 week ago:</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full"><div className="h-2 bg-indigo-500 rounded-full" style={{width: '35%'}}></div></div>
                    <span className="text-sm font-medium text-gray-800 ml-3">35%</span>
                </div>
                 <div className="flex items-center">
                    <div className="h-2 w-2 rounded-full bg-gray-400 mr-2"></div>
                    <span className="text-sm text-gray-600 mr-3 w-28">Today:</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full"><div className="h-2 bg-indigo-500 rounded-full" style={{width: '70%'}}></div></div>
                    <span className="text-sm font-medium text-gray-800 ml-3">70%</span>
                </div>
                 <div className="flex items-center">
                    <div className="h-2 w-2 rounded-full bg-gray-400 mr-2"></div>
                    <span className="text-sm text-gray-600 mr-3 w-28">Projected (1 week):</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full"><div className="h-2 bg-indigo-500 rounded-full" style={{width: '85%'}}></div></div>
                    <span className="text-sm font-medium text-gray-800 ml-3">85%</span>
                </div>
             </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RecommendationsPage;